import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Star, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';

const AdminTestimonials = () => {
  const { toast } = useToast();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '', rating: '5', comment_en: '', comment_ar: ''
  });

  const loadTestimonials = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/testimonials');
      setTestimonials(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load testimonials", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadTestimonials(); }, [loadTestimonials]);

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ customer_name: '', rating: '5', comment_en: '', comment_ar: '' });
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const comment = item.comment || {};
    setFormData({
      customer_name: item.customer_name,
      rating: item.rating.toString(),
      comment_en: typeof comment === 'object' ? (comment.en || '') : comment,
      comment_ar: typeof comment === 'object' ? (comment.ar || '') : ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        customer_name: formData.customer_name,
        rating: parseInt(formData.rating),
        comment: { en: formData.comment_en, ar: formData.comment_ar }
      };

      if (editingItem) {
        await adminApi.put(`/testimonials/${editingItem.id}`, data);
        toast({ title: "Success", description: "Testimonial updated" });
      } else {
        await adminApi.post('/testimonials', data);
        toast({ title: "Success", description: "Testimonial added" });
      }

      setIsDialogOpen(false);
      resetForm();
      loadTestimonials();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save testimonial", variant: "destructive" });
    }
  };

  const handleToggleApproval = async (item) => {
    try {
      await adminApi.put(`/testimonials/${item.id}`, { is_approved: !item.is_approved });
      toast({ title: "Success", description: `Testimonial ${!item.is_approved ? 'approved' : 'hidden'}` });
      loadTestimonials();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await adminApi.delete(`/testimonials/${id}`);
      toast({ title: "Success", description: "Testimonial deleted" });
      loadTestimonials();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  return (
    <div data-testid="admin-testimonials-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Testimonials Management</h2>
          <p className="text-gray-600 mt-1">Manage customer reviews and ratings</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700 w-full sm:w-auto" data-testid="add-testimonial-btn" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />Add Testimonial
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsDialogOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Testimonial' : 'Add New Testimonial'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Customer Name</Label>
              <Input data-testid="testimonial-name" value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} required />
            </div>
            <div>
              <Label>Rating (1-5)</Label>
              <select
                data-testid="testimonial-rating"
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {[5, 4, 3, 2, 1].map(r => (
                  <option key={r} value={r}>{r} Star{r > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Comment (English)</Label>
              <Textarea data-testid="testimonial-comment-en" value={formData.comment_en} onChange={(e) => setFormData({ ...formData, comment_en: e.target.value })} rows={3} required />
            </div>
            <div>
              <Label>Comment (Arabic)</Label>
              <Textarea data-testid="testimonial-comment-ar" value={formData.comment_ar} onChange={(e) => setFormData({ ...formData, comment_ar: e.target.value })} rows={3} dir="rtl" />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-testimonial-btn">
                {editingItem ? 'Update' : 'Add Testimonial'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No testimonials yet. Add your first one!</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((item) => (
            <Card key={item.id} className="relative" data-testid={`testimonial-item-${item.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`h-4 w-4 ${i < item.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <Badge variant={item.is_approved ? "default" : "secondary"} className={item.is_approved ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                    {item.is_approved ? 'Visible' : 'Hidden'}
                  </Badge>
                </div>
                <p className="text-gray-700 mb-4 italic text-sm">"{typeof item.comment === 'object' ? (item.comment.en || item.comment.ar || '') : item.comment}"</p>
                <div className="font-semibold text-gray-900 mb-4">{item.customer_name}</div>
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleToggleApproval(item)} data-testid={`toggle-testimonial-${item.id}`}>
                    {item.is_approved ? <XCircle className="h-4 w-4 mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                    {item.is_approved ? 'Hide' : 'Approve'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleEdit(item)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
